import {
  Checkbox,
  Grid,
  IconButton,
  ListItemText,
  MenuItem,
  NativeSelect,
  Paper,
  Select,
  Tooltip,
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import MaterialTable from 'material-table';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useHistory, useLocation } from 'react-router-dom';

import Loading from '../../components/Loading';
import Header from '../../layout/Header';
import Subheader from '../../layout/Subheader';
import api from '../../services/api';
import { findPermission } from '../../services/auth';
import { materialTableLocalizationPtBR } from '../../services/materialTableLocalization';
import { consultarPorAncoraId } from '../../services/ancorasImportacoesTitulosService';
import { consultar as consultarErps } from '../../services/erpsService';
import { validatePeriodoRange } from '../../utils/dateRangeValidation';

import ExecucaoDetalhesModal from './ExecucaoDetalhesModal';
import useStyles from './style';

// ModuloId derivado via dbo.fnCalculaModuloId('ImportacoesTitulosExecucoes').
export const MODULO_ID_IMPORTACOES_EXECUCOES = 40102;
// Tela de auditoria: aceita ate 90 dias em vez do default 31d das listagens
// operacionais — permite consultar historico de mes anterior sem paginacao
// pesada. Ajuste do finding G5 da auditoria.
const PERIODO_MAX_DIAS = 90;
const AUTO_REFRESH_MS = 15000;

// Filtro de data abre sempre no dia atual (nao um range de 90d) — usuario so
// alarga o periodo quando precisa.
const getHojeIso = () => new Date().toISOString().slice(0, 10);

// Enums espelhados do backend. API global serializa enums como string
// (JsonStringEnumConverter), entao casamos por `name`.
const STATUS_OPTIONS = [
  { value: 1, name: 'EmExecucao',        label: 'Em Execução',         color: '#FB8832' },
  { value: 2, name: 'Sucesso',           label: 'Sucesso',             color: '#4CAF50' },
  { value: 3, name: 'Falha',             label: 'Falha',               color: '#F44336' },
  { value: 4, name: 'SucessoComAvisos',  label: 'Sucesso c/ Avisos',   color: '#2196F3' },
];

const TIPO_OPTIONS = [
  { value: 1, name: 'Full',         label: 'Full' },
  { value: 2, name: 'Complementar', label: 'Complementar' },
];

const enumToInt = (options, s) => {
  if (typeof s === 'number') return s;
  const found = options.find((x) => x.name === s);
  return found ? found.value : -1;
};

const statusToInt  = (s) => enumToInt(STATUS_OPTIONS, s);
const statusLabel  = (s) => STATUS_OPTIONS.find((x) => x.value === statusToInt(s))?.label || s;
const statusColor  = (s) => STATUS_OPTIONS.find((x) => x.value === statusToInt(s))?.color || '#777';
const tipoLabel    = (s) => TIPO_OPTIONS.find((x) => x.name === s || x.value === s)?.label || s || '-';

const formatDateTime = (d) => {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleString('pt-BR');
  } catch (_) {
    return '-';
  }
};

const formatDuracao = (segundos) => {
  if (segundos === null || segundos === undefined) return '-';
  const s = Number(segundos);
  if (Number.isNaN(s)) return '-';
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s - m * 60);
  return `${m}m ${r}s`;
};

export function ImportacoesTitulosExecucoes() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const [isLoading, setLoading] = useState(false);
  const [ancoras, setAncoras] = useState([]);
  const [vinculos, setVinculos] = useState([]);
  const [erps, setErps] = useState([]);
  const [rows, setRows] = useState([]);

  const [ancoraId, setAncoraId] = useState('');
  const [ancoraImportacaoTituloApiErpId, setAncoraImportacaoTituloApiErpId] = useState('');
  const [erpFiltro, setErpFiltro] = useState('');
  const [dataInicial, setDataInicial] = useState(getHojeIso);
  const [dataFinal, setDataFinal] = useState(getHojeIso);
  const [statusFiltro, setStatusFiltro] = useState([]);
  const [tipoFiltro, setTipoFiltro] = useState('');

  const [detalheModal, setDetalheModal] = useState({ open: false, execucao: null });

  // Ref para o intervalo de auto-refresh; usamos ref para poder limpar
  // dentro do effect sem re-disparar.
  const refreshTimerRef = useRef(null);
  const hasSearchedRef = useRef(false);

  useEffect(() => {
    if (!findPermission(MODULO_ID_IMPORTACOES_EXECUCOES, 'Consultar')) {
      history.replace('/app/home');
    }
  }, [history]);

  const fetchAncoras = async () => {
    try {
      const res = await api.get('Ancoras/Consultar?status=Ativo');
      const lista = res?.data?.resposta || [];
      lista.sort((a, b) => (a.apelido || '').localeCompare(b.apelido || '', 'pt-BR'));
      setAncoras(lista);
    } catch (e) {
      toast.error('Erro ao carregar redes.');
    }
  };

  const fetchErps = async () => {
    try {
      const res = await consultarErps({ itensPorPagina: 200 });
      const lista = res?.data?.resposta || [];
      const nomes = [...new Set(lista.map((e) => e.erp).filter(Boolean))];
      nomes.sort((a, b) => a.localeCompare(b, 'pt-BR'));
      setErps(nomes);
    } catch (e) {
      setErps([]);
    }
  };

  const fetchVinculos = async (aid) => {
    if (!aid) {
      setVinculos([]);
      return;
    }
    try {
      const lista = await consultarPorAncoraId(aid);
      setVinculos(lista);
    } catch (e) {
      setVinculos([]);
    }
  };

  const fetchExecucoes = async (opts = { silent: false }) => {
    const periodo = validatePeriodoRange(dataInicial, dataFinal, PERIODO_MAX_DIAS);
    if (!periodo.valid) {
      if (!opts.silent) toast.warn(periodo.mensagem);
      return;
    }
    if (!opts.silent) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (ancoraImportacaoTituloApiErpId) {
        params.append('ancoraImportacaoTituloApiErpId', ancoraImportacaoTituloApiErpId);
      }
      if (ancoraId) params.append('ancoraId', ancoraId);
      params.append('dataInicial', dataInicial);
      params.append('dataFinal', dataFinal);
      statusFiltro.forEach((s) => params.append('status', s));
      if (tipoFiltro) params.append('tipo', tipoFiltro);
      if (erpFiltro) params.append('erp', erpFiltro);

      const res = await api.get(`ImportacaoTitulos/Execucoes?${params.toString()}`);
      setRows(res?.data?.resposta || []);
      hasSearchedRef.current = true;
    } catch (e) {
      if (!opts.silent) {
        toast.error('Erro ao consultar execuções.');
        setRows([]);
      }
    }
    if (!opts.silent) setLoading(false);
  };

  // Auto-refresh: se ha alguma linha EmExecucao (status 1), reagenda um refetch
  // silencioso a cada 15s. Cleanup do intervalo previne leak quando as linhas
  // encerram, o componente desmonta ou os filtros mudam.
  useEffect(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    const temEmExecucao = rows.some((r) => statusToInt(r.status) === 1);
    if (!temEmExecucao || !hasSearchedRef.current) return undefined;

    refreshTimerRef.current = setInterval(() => {
      fetchExecucoes({ silent: true });
    }, AUTO_REFRESH_MS);

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [rows]);

  useEffect(() => {
    fetchAncoras();
    fetchErps();
    // Deep-link vindo do accordion "Integracoes de Importacao" no Edit da Ancora.
    const params = new URLSearchParams(location.search);
    const ancoraParam = params.get('ancoraId');
    if (ancoraParam) {
      setAncoraId(ancoraParam);
      toast.info('Rede pré-preenchida. Clique em Pesquisar.');
    }
  }, []);

  useEffect(() => {
    fetchVinculos(ancoraId);
    // Reseta o vinculo se a ancora mudou
    setAncoraImportacaoTituloApiErpId('');
  }, [ancoraId]);

  const temEmExecucao = useMemo(
    () => rows.some((r) => statusToInt(r.status) === 1),
    [rows],
  );

  const columns = useMemo(
    () => [
      { title: 'Início',      field: 'dataInicio', render: (r) => formatDateTime(r.dataInicio) },
      { title: 'Fim',         field: 'dataFim',    render: (r) => formatDateTime(r.dataFim) },
      { title: 'Rede',        field: 'apelidoAncora',      render: (r) => r.apelidoAncora || '-' },
      { title: 'Vínculo',     field: 'apelidoVinculo',     render: (r) => r.apelidoVinculo || '-' },
      { title: 'ERP',         field: 'erp',                render: (r) => r.erp || '-' },
      {
        title: 'Tipo',
        field: 'tipo',
        render: (r) => <span className={classes.tipoChip}>{tipoLabel(r.tipo)}</span>,
      },
      {
        title: 'Origem',
        field: 'origem',
        render: (r) => <span className={classes.origemChip}>{r.origem || '-'}</span>,
      },
      {
        title: 'Status',
        field: 'status',
        render: (r) => (
          <span className={classes.statusChip} style={{ background: statusColor(r.status) }}>
            {statusLabel(r.status)}
          </span>
        ),
      },
      { title: 'Duplicatas',   field: 'qtdDuplicatas',   render: (r) => r.qtdDuplicatas ?? '-' },
      { title: 'Fornecedores', field: 'qtdFornecedores', render: (r) => r.qtdFornecedores ?? '-' },
      { title: 'Duração',      field: 'duracaoSegundos', render: (r) => formatDuracao(r.duracaoSegundos) },
      {
        title: 'Ações',
        field: 'acoes',
        sorting: false,
        render: (r) => (
          <Tooltip title="Ver detalhes">
            <IconButton
              size="small"
              onClick={() => setDetalheModal({ open: true, execucao: r })}
            >
              <VisibilityIcon fontSize="small" style={{ color: '#026894' }} />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [classes],
  );

  return (
    <div className={classes.root}>
      <Header />
      <Subheader title="Histórico de Importações ERP" back="/app/home" />
      <div className={classes.content}>
        <div className={classes.rootForm}>
          <header>
            <Grid container className={classes.searchContainer} spacing={2}>
              <Grid item xs={3} className={classes.item}>
                <label className={classes.label}>Rede</label>
                <Paper className={classes.medium} elevation={0}>
                  <NativeSelect
                    className={classes.input}
                    value={ancoraId}
                    onChange={(e) => setAncoraId(e.target.value)}
                  >
                    <option value="">Todas</option>
                    {ancoras.map((a) => (
                      <option key={a.ancoraId} value={a.ancoraId}>
                        {a.apelido}
                      </option>
                    ))}
                  </NativeSelect>
                </Paper>
              </Grid>
              <Grid item xs={3} className={classes.item}>
                <label className={classes.label}>Vínculo</label>
                <Paper className={classes.medium} elevation={0}>
                  <NativeSelect
                    className={classes.input}
                    value={ancoraImportacaoTituloApiErpId}
                    onChange={(e) => setAncoraImportacaoTituloApiErpId(e.target.value)}
                    disabled={!ancoraId}
                  >
                    <option value="">Todos</option>
                    {vinculos.map((v) => (
                      <option
                        key={v.ancoraImportacaoTituloApiErpId}
                        value={v.ancoraImportacaoTituloApiErpId}
                      >
                        {v.apelido || v.nomeErp || `Vínculo ${v.ancoraImportacaoTituloApiErpId}`}
                      </option>
                    ))}
                  </NativeSelect>
                </Paper>
              </Grid>
              <Grid item xs={2} className={classes.item}>
                <label className={classes.label}>Data Inicial *</label>
                <Paper className={classes.medium} elevation={0}>
                  <input
                    type="date"
                    className={classes.input}
                    value={dataInicial}
                    onChange={(e) => setDataInicial(e.target.value)}
                  />
                </Paper>
              </Grid>
              <Grid item xs={2} className={classes.item}>
                <label className={classes.label}>Data Final *</label>
                <Paper className={classes.medium} elevation={0}>
                  <input
                    type="date"
                    className={classes.input}
                    value={dataFinal}
                    onChange={(e) => setDataFinal(e.target.value)}
                  />
                </Paper>
              </Grid>
              <Grid item xs={2} className={classes.item}>
                <label className={classes.label}>ERP</label>
                <Paper className={classes.medium} elevation={0}>
                  <NativeSelect
                    className={classes.input}
                    value={erpFiltro}
                    onChange={(e) => setErpFiltro(e.target.value)}
                  >
                    <option value="">Todos</option>
                    {erps.map((e) => (
                      <option key={e} value={e}>
                        {e}
                      </option>
                    ))}
                  </NativeSelect>
                </Paper>
              </Grid>
              <Grid item xs={2} className={classes.item}>
                <label className={classes.label}>Tipo</label>
                <Paper className={classes.medium} elevation={0}>
                  <NativeSelect
                    className={classes.input}
                    value={tipoFiltro}
                    onChange={(e) => setTipoFiltro(e.target.value)}
                  >
                    <option value="">Ambos</option>
                    {TIPO_OPTIONS.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </NativeSelect>
                </Paper>
              </Grid>
              <Grid item xs={3} className={classes.item}>
                <label className={classes.label}>Status</label>
                <Paper className={classes.medium} elevation={0}>
                  <Select
                    multiple
                    disableUnderline
                    displayEmpty
                    className={classes.input}
                    value={statusFiltro}
                    onChange={(e) => setStatusFiltro(e.target.value)}
                    renderValue={(selected) => {
                      if (!selected || selected.length === 0) return 'Todos';
                      if (selected.length === STATUS_OPTIONS.length) return 'Todos';
                      if (selected.length === 1) return statusLabel(selected[0]);
                      return `${selected.length} selecionados`;
                    }}
                    MenuProps={{
                      variant: 'menu',
                      getContentAnchorEl: null,
                      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                      transformOrigin: { vertical: 'top', horizontal: 'left' },
                      autoFocus: false,
                      PaperProps: {
                        style: { maxHeight: 220, overflowY: 'scroll', width: 240 },
                      },
                      MenuListProps: { style: { padding: 0, width: 240 } },
                    }}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <MenuItem key={s.value} value={s.value} dense style={{ paddingLeft: 4 }}>
                        <Checkbox
                          checked={statusFiltro.indexOf(s.value) > -1}
                          size="small"
                          style={{ color: s.color, padding: 4 }}
                        />
                        <span
                          style={{
                            display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                            background: s.color, marginRight: 8, flexShrink: 0,
                          }}
                        />
                        <ListItemText
                          primary={s.label}
                          primaryTypographyProps={{ style: { fontSize: 13 } }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </Paper>
              </Grid>
            </Grid>
          </header>
          <div className={classes.actions}>
            {temEmExecucao ? (
              <span className={classes.autoRefreshBadge}>
                Auto-refresh ativo (15s)
              </span>
            ) : null}
            <button
              className={classes.buttonFilter}
              onClick={() => fetchExecucoes({ silent: false })}
            >
              Pesquisar
            </button>
          </div>
          {isLoading ? (
            <Loading />
          ) : (
            <>
              <MaterialTable
                options={{
                  showTitle: false,
                  search: false,
                  toolbar: false,
                  paging: false,
                  filtering: false,
                  headerStyle: { backgroundColor: '#F5F5F5', fontSize: '13px' },
                  rowStyle: { fontSize: '13px' },
                }}
                localization={materialTableLocalizationPtBR}
                columns={columns}
                data={rows}
              />
              <div className={classes.footer}>
                <div className={classes.totals}>
                  <span>{rows.length} execução(ões) exibida(s)</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <ExecucaoDetalhesModal
        open={detalheModal.open}
        execucao={detalheModal.execucao}
        onClose={() => setDetalheModal({ open: false, execucao: null })}
      />
    </div>
  );
}

export default ImportacoesTitulosExecucoes;
